import { useEffect, useMemo, useState } from "react";
import { Search, X, TreePalm, Stethoscope, Thermometer, Repeat, CalendarOff } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { toneOf, todayIso, czShort } from "@/lib/absences";

const ICON = { dovolena: TreePalm, lekar: Stethoscope, nemoc: Thermometer, nahradni_volno: Repeat, neplacene_volno: CalendarOff };
const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

// Kdo má v období [from, to] dovolenou, lékaře, nemoc… (179). Vidí vedení —
// jiným rolím backend vrátí jen jejich vlastní, takže štítky prostě nebudou.
export function useAbsenceMap(from, to, enabled = true) {
  const [map, setMap] = useState({});
  const a = from || todayIso();
  const b = to && to >= a ? to : a;
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    api.get(`/absences?date_from=${a}&date_to=${b}&status=approved,pending`)
      .then((r) => {
        if (!live) return;
        const m = {};
        for (const x of r.data.items || []) (m[x.user_id] = m[x.user_id] || []).push(x);
        setMap(m);
      })
      .catch(() => live && setMap({}));
    return () => { live = false; };
  }, [a, b, enabled]);
  return map;
}

// Štítek nepřítomnosti: PC celý popis, mobil jen ikona (popis v title / aria-label).
export function AbsenceBadge({ items }) {
  if (!items?.length) return null;
  const x = items[0];
  const Icon = ICON[x.type] || CalendarOff;
  const when = x.date_from === x.date_to ? czShort(x.date_from) : `${czShort(x.date_from)}–${czShort(x.date_to)}`;
  const text = `${x.type_label}${x.part === "half" ? " (půl dne)" : x.part === "time" ? ` ${x.t_from}–${x.t_to}` : ""} · ${when}${x.status === "pending" ? " · čeká" : ""}`;
  return (
    <span title={text} aria-label={text} data-testid="absence-badge"
      className={`inline-flex items-center gap-1 h-6 px-1.5 md:px-2 rounded-full border text-[11.5px] font-bold whitespace-nowrap shrink-0 ${toneOf(x.code)} ${x.status === "pending" ? "border-dashed" : ""}`}>
      <Icon size={13} className="shrink-0" /><span className="hidden md:inline">{text}</span>
    </span>
  );
}

// Výběr lidí s hledáním a štítky nepřítomnosti (179) — úkoly i hromadný zápis.
export function PeoplePicker({ people, selected, onToggle, absences = {}, sub, meLabel, testid = "people", maxH = "max-h-56", rowTestid, compact = false, leadId, disabled }) {
  const [q, setQ] = useState("");
  const list = useMemo(() => {
    const n = norm(q.trim());
    return n ? people.filter((p) => norm(`${p.name} ${p.position || ""}`).includes(n)) : people;
  }, [people, q]);
  const showSearch = people.length > 6;
  return (
    <div className="border border-border bg-card rounded-[14px] overflow-hidden" data-testid={testid}>
      {showSearch && (
        <div className="p-2 border-b border-border">
          <div className="h-10 rounded-[10px] bg-secondary/60 px-3 flex items-center gap-2">
            <Search size={15} className="text-muted-foreground shrink-0" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat jméno…" aria-label="Hledat člověka"
              className="bg-transparent focus:outline-none w-full min-w-0 text-[15px]" data-testid={`${testid}-search`} />
            {q && <button type="button" onClick={() => setQ("")} aria-label="Smazat hledání" className="text-muted-foreground"><X size={15} /></button>}
          </div>
        </div>
      )}
      <div className={`${maxH} overflow-y-auto overscroll-contain divide-y divide-border`}>
        {list.map((p) => { const off = !!disabled?.(p); return (
          <label key={p.id} className={`flex items-center gap-3 px-3 ${compact ? "py-2 min-h-[44px]" : "py-2.5 min-h-[52px]"} ${off ? "opacity-55 cursor-not-allowed" : "cursor-pointer hover:bg-secondary/40"}`}
            data-testid={rowTestid ? rowTestid(p) : `${testid}-${p.id}`} aria-disabled={off || undefined}>
            <Checkbox checked={selected.includes(p.id)} disabled={off} onCheckedChange={() => !off && onToggle(p.id)} />
            <span className="flex-1 min-w-0">
              <span className="block font-semibold truncate text-[14.5px]">{p.name}{meLabel?.(p) || ""}</span>
              {sub?.(p) && <span className="block text-[12.5px] text-muted-foreground truncate">{sub(p)}</span>}
            </span>
            {leadId === p.id && selected.length > 1 && (
              <span className="h-6 px-2 rounded-full bg-brand/15 text-brand text-[11.5px] font-bold inline-flex items-center shrink-0" data-testid="people-lead">odpovídá</span>
            )}
            <AbsenceBadge items={absences[p.id]} />
          </label>
        ); })}
        {list.length === 0 && <div className="p-3 text-sm text-muted-foreground">{q ? "Nikoho takového nenacházím." : "Nikdo k výběru."}</div>}
      </div>
    </div>
  );
}
